import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function Homework() {
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [students, setStudents] = useState([])
  const [homework, setHomework] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selStudent, setSelStudent] = useState(null)
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState('done')
  const [note, setNote] = useState('')

  useEffect(() => {
    fetchGroups()
  }, [])

  useEffect(() => {
    if (selectedGroup) {
      fetchStudents()
      fetchHomework()
    }
  }, [selectedGroup])

  async function fetchGroups() {
    const { data } = await supabase
      .from('groups')
      .select('*')
      .order('grade_level')
      .order('name')

    setGroups(data || [])

    if (data?.length) {
      setSelectedGroup(data[0])
    }

    setLoading(false)
  }

  async function fetchStudents() {
    const { data } = await supabase
      .from('students')
      .select('*')
      .eq('group_id', selectedGroup.id)
      .order('name')

    setStudents(data || [])
  }

  async function fetchHomework() {
    const { data: studs } = await supabase
      .from('students')
      .select('id')
      .eq('group_id', selectedGroup.id)

    if (!studs?.length) {
      setHomework([])
      return
    }

    const { data } = await supabase
      .from('homework')
      .select('*, students(name)')
      .in('student_id', studs.map(s => s.id))
      .order('created_at', { ascending: false })

    setHomework(data || [])
  }

  async function addHomework() {
    if (!selStudent) {
      alert('Please select a student')
      return
    }

    if (!title.trim()) {
      alert('Please enter homework title')
      return
    }

    const { error } = await supabase
      .from('homework')
      .insert({
        student_id: selStudent.id,
        title: title.trim(),
        status,
        date: new Date().toISOString().split('T')[0],
        note: note.trim()
      })

    if (error) {
      alert(error.message)
      return
    }

    fetchHomework()
    setShowModal(false)
    setTitle('')
    setNote('')
    setStatus('done')
    setSelStudent(null)
  }

  async function deleteHomework(id) {
    if (!window.confirm('Delete this record?')) return

    await supabase
      .from('homework')
      .delete()
      .eq('id', id)

    setHomework(prev => prev.filter(h => h.id !== id))
  }

  const statusCfg = {
    done: {
      label: 'Submitted',
      cls: 'badge-success'
    },
    late: {
      label: 'Late',
      cls: 'badge-warning'
    },
    missing: {
      label: 'Not submitted',
      cls: 'badge-danger'
    },
  }

  if (loading) {
    return <div className="empty">Loading...</div>
  }

  return (
    <div>

      {/* Mobile/desktop visibility for this page */}
      <style>{`
        .homework-desktop-view {
          display: block;
        }

        .homework-mobile-view {
          display: none;
        }

        .homework-mobile-card {
          background: var(--c-surface);
          border: 1px solid var(--c-border);
          border-radius: 14px;
          padding: 14px;
          margin-bottom: 10px;
          box-shadow: var(--c-shadow);
        }

        .homework-mobile-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }

        .homework-mobile-note {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid var(--c-border);
        }

        .homework-mobile-note-label {
          font-size: 11px;
          color: var(--c-text3);
          margin-bottom: 4px;
          font-weight: 600;
        }

        .homework-mobile-note-text {
          font-size: 13px;
          color: var(--c-text2);
          overflow-wrap: anywhere;
          word-break: break-word;
          line-height: 1.5;
        }

        @media (max-width: 767px) {
          .homework-desktop-view {
            display: none !important;
          }

          .homework-mobile-view {
            display: block !important;
          }
        }
      `}</style>

      <div className="page-header">
        <h2>Homework</h2>

        <button
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
        >
          + Add Record
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 16
        }}
      >
        {groups.map(g => (
          <button
            key={g.id}
            className={`chip ${
              selectedGroup?.id === g.id ? 'active' : ''
            }`}
            onClick={() => setSelectedGroup(g)}
          >
            {g.name}
          </button>
        ))}
      </div>

      {homework.length === 0 ? (
        <div className="empty">
          No homework records
        </div>
      ) : (
        <>

          {/* ================= DESKTOP ================= */}
          <div className="homework-desktop-view">
            <div
              className="card"
              style={{
                padding: 0,
                overflow: 'hidden'
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Homework</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Note</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {homework.map(h => (
                    <tr key={h.id}>

                      <td style={{ fontWeight: 500 }}>
                        {h.students?.name}
                      </td>

                      <td>
                        {h.title}
                      </td>

                      <td
                        style={{
                          color: 'var(--text3)'
                        }}
                      >
                        {h.date}
                      </td>

                      <td>
                        <span
                          className={`badge ${
                            statusCfg[h.status]?.cls ||
                            'badge-warning'
                          }`}
                        >
                          {statusCfg[h.status]?.label ||
                            h.status}
                        </span>
                      </td>

                      <td
                        style={{
                          color: 'var(--text3)',
                          maxWidth: 300,
                          overflowWrap: 'anywhere'
                        }}
                      >
                        {h.note || '—'}
                      </td>

                      <td>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            deleteHomework(h.id)
                          }
                        >
                          Delete
                        </button>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================= MOBILE ================= */}
          <div className="homework-mobile-view">

            {homework.map(h => (

              <div
                key={h.id}
                className="homework-mobile-card"
              >

                <div className="homework-mobile-row">

                  <div
                    style={{
                      minWidth: 0,
                      flex: 1
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {h.students?.name}
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        color: 'var(--text2)',
                        marginTop: 4,
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {h.title}
                    </div>
                  </div>

                  <span
                    className={`badge ${
                      statusCfg[h.status]?.cls ||
                      'badge-warning'
                    }`}
                    style={{
                      flexShrink: 0
                    }}
                  >
                    {statusCfg[h.status]?.label ||
                      h.status}
                  </span>

                </div>

                <div
                  className="homework-mobile-row"
                  style={{
                    marginTop: 10,
                    alignItems: 'center'
                  }}
                >

                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--text3)'
                    }}
                  >
                    {h.date}
                  </span>

                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() =>
                      deleteHomework(h.id)
                    }
                  >
                    Delete
                  </button>

                </div>

                {/* NOTE — مهم جدًا */}
                <div className="homework-mobile-note">

                  <div className="homework-mobile-note-label">
                    Note
                  </div>

                  <div className="homework-mobile-note-text">
                    {h.note || '—'}
                  </div>

                </div>

              </div>

            ))}

          </div>

        </>
      )}

      {/* ================= ADD HOMEWORK MODAL ================= */}
      {showModal && (
        <div
          className="overlay"
          onClick={e =>
            e.target === e.currentTarget &&
            setShowModal(false)
          }
        >

          <div className="modal">

            <div className="modal-title">
              Add Homework Record
            </div>

            <div className="field">

              <label>
                Select Student *
              </label>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap'
                }}
              >

                {students.map(s => (
                  <button
                    key={s.id}
                    className={`chip ${
                      selStudent?.id === s.id
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      setSelStudent(s)
                    }
                  >
                    {s.name}
                  </button>
                ))}

              </div>

              {students.length === 0 && (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--danger)',
                    marginTop: 6
                  }}
                >
                  No students in this group
                </div>
              )}

            </div>

            <div className="field">

              <label>
                Homework Title *
              </label>

              <input
                value={title}
                onChange={e =>
                  setTitle(e.target.value)
                }
                placeholder="e.g. Page 45 exercises"
              />

            </div>

            <div className="field">

              <label>
                Status
              </label>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap'
                }}
              >

                {Object.entries(statusCfg).map(
                  ([key, cfg]) => (
                    <button
                      key={key}
                      className={`chip ${
                        status === key
                          ? 'active'
                          : ''
                      }`}
                      onClick={() =>
                        setStatus(key)
                      }
                    >
                      {cfg.label}
                    </button>
                  )
                )}

              </div>

            </div>

            <div className="field">

              <label>
                Note (optional)
              </label>

              <input
                value={note}
                onChange={e =>
                  setNote(e.target.value)
                }
                placeholder="Any note..."
              />

            </div>

            <div className="modal-actions">

              <button
                className="btn"
                onClick={() =>
                  setShowModal(false)
                }
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={addHomework}
              >
                Save
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  )
}